import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, of } from 'rxjs';
import { catchError, finalize, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { GlobalLoaderService } from '../../core/loading/global-loader.service';
import { BrokersService } from '../../shared/services/admin/brokers.service';
import { ApiService } from '../../shared/services/api/api.service';
import { Broker } from '../../shared/services/admin/brokers.service';
import * as BrokersActions from './brokers.actions';
import { selectAllBrokers, selectBrokersLastLoaded } from './brokers.selectors';

@Injectable()
export class BrokersEffects {
  private actions$ = inject(Actions);
  private brokersService = inject(BrokersService);
  private apiService = inject(ApiService);
  private store = inject(Store);
  private globalLoader = inject(GlobalLoaderService);

  loadBrokers$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BrokersActions.loadBrokers),
        withLatestFrom(
          this.store.select(selectAllBrokers),
          this.store.select(selectBrokersLastLoaded),
        ),
        switchMap(([action, brokers, lastLoaded]) => {
          // Check cache - if data is less than 5 minutes old and we have brokers, don't reload
          const now = Date.now();
          const cachedBrokers = brokers as Broker[];
          if (
            lastLoaded &&
            cachedBrokers &&
            cachedBrokers.length > 0 &&
            now - lastLoaded < 5 * 60 * 1000
          ) {
            // Cached path: no HTTP so the loading interceptor does not run — mirror begin/end so the global loader still shows.
            return defer(() => {
              this.globalLoader.begin();
              return of(BrokersActions.loadBrokersSuccess({ brokers: cachedBrokers })).pipe(
                finalize(() => this.globalLoader.end()),
              );
            });
          }

          return this.brokersService.getBrokers(false).pipe(
            map((brokers) => BrokersActions.loadBrokersSuccess({ brokers })),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to load brokers', 'Error');
              return of(BrokersActions.loadBrokersFailure({ error }));
            }),
          );
        }),
      );
    },
    { functional: false },
  );

  createBroker$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BrokersActions.createBroker),
        switchMap(({ broker }) =>
          this.brokersService.createBroker(broker).pipe(
            map((createdBroker) => {
              this.apiService.showSuccess('Broker created successfully', 'Success');
              return BrokersActions.createBrokerSuccess({ broker: createdBroker });
            }),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to create broker', 'Error');
              return of(BrokersActions.createBrokerFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );

  updateBroker$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BrokersActions.updateBroker),
        switchMap(({ brokerId, update }) =>
          this.brokersService.updateBroker(brokerId, update).pipe(
            map((updatedBroker) => {
              this.apiService.showSuccess('Broker updated successfully', 'Success');
              return BrokersActions.updateBrokerSuccess({ broker: updatedBroker });
            }),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to update broker', 'Error');
              return of(BrokersActions.updateBrokerFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );

  deleteBroker$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(BrokersActions.deleteBroker),
        switchMap(({ brokerId }) =>
          this.brokersService.deleteBroker(brokerId).pipe(
            map(() => {
              this.apiService.showSuccess('Broker deleted successfully', 'Success');
              return BrokersActions.deleteBrokerSuccess({ brokerId });
            }),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to delete broker', 'Error');
              return of(BrokersActions.deleteBrokerFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );
}
